import { Controller } from '@hotwired/stimulus';

/**
 * Scroll Reveal Controller
 *
 * Fades elements in as they scroll into view. Purely decorative.
 *
 * Content starts at `opacity: 0`, so if this controller never runs the content
 * would be invisible — an accessibility failure, not just a missing animation.
 * Two guards prevent that: the hidden state is only applied once the controller
 * connects (so no-JS keeps everything visible), and `prefers-reduced-motion`
 * short-circuits to fully visible without observing anything (WCAG 2.3.3).
 *
 * Usage:
 *   <div data-controller="scroll-reveal">
 *     <div data-scroll-reveal-target="item">…</div>
 *     <div data-scroll-reveal-target="item">…</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ['item'];

  declare itemTargets: HTMLElement[];

  private observer?: IntersectionObserver;

  connect() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      this.itemTargets.forEach((item) => item.classList.add('is-revealed'));
      return;
    }

    // Only hide now that we know we can reveal again.
    this.itemTargets.forEach((item) => item.classList.add('is-revealing'));

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          this.observer?.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -8% 0px' },
    );

    this.itemTargets.forEach((item) => this.observer?.observe(item));
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = undefined;
  }
}
